import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, InputNumber, Button, message } from "antd";
import { Booking } from "../../../types/booking";
import { supabase } from "../../../lib/supabase";

interface PricingPaymentTerm {
  id: number;
  days: number;
  interest_vat_import_duties: number;
  created_at: string;
}

interface CustomsSectionProps {
  selectedBooking: Booking | null;
  onAddCustomsLine: (line: {
    description: string;
    quantity: number;
    unit_price: number;
    vat_percentage: number;
    cost_type: string;
  }) => void;
}

const CustomsSection: React.FC<CustomsSectionProps> = ({
  selectedBooking,
  onAddCustomsLine,
}) => {
  const [paymentTerm, setPaymentTerm] = useState<PricingPaymentTerm | null>(
    null
  );
  const [btwValues, setBtwValues] = useState({
    quantity: 1,
    unitPrice: 0,
    vatPercentage: 0,
  });
  const [invoerrechtenValues, setInvoerrechtenValues] = useState({
    quantity: 1,
    unitPrice: 0,
    vatPercentage: 0,
  });

  // Fetch payment terms when booking changes
  useEffect(() => {
    const fetchPaymentTerms = async () => {
      if (!selectedBooking) {
        setPaymentTerm(null);
        return;
      }

      try {
        let paymentTermId = 1; // Default payment term ID

        // Check if company has payment terms
        if (selectedBooking.company_info) {
          const company = Array.isArray(selectedBooking.company_info)
            ? selectedBooking.company_info[0]
            : selectedBooking.company_info;

          if (company?.pricing_payment_term) {
            paymentTermId = company.pricing_payment_term;
          }
        }

        // Fetch the payment term details
        const { data: pricingTerm, error } = await supabase
          .from("pricing_payment_terms")
          .select("*")
          .eq("id", paymentTermId)
          .single();

        if (!error && pricingTerm) {
          setPaymentTerm(pricingTerm);
        } else {
          console.error("Error fetching payment terms:", error);
          // Fallback to default
          setPaymentTerm({
            id: 1,
            days: 30,
            interest_vat_import_duties: 0,
            created_at: "",
          });
        }
      } catch (error) {
        console.error("Error in fetchPaymentTerms:", error);
      }
    };

    fetchPaymentTerms();
  }, [selectedBooking]);

  // Add BTW bij Invoer to invoice lines
  const addBtwToInvoiceLines = () => {
    if (btwValues.unitPrice === 0) {
      message.error("Please enter a unit price for BTW bij Invoer");
      return;
    }

    // Main BTW bij Invoer line (original amount)
    const mainLine = {
      description: "BTW bij Invoer",
      quantity: btwValues.quantity,
      unit_price: Math.round(btwValues.unitPrice * 100) / 100, // Round to 2 decimals
      vat_percentage: btwValues.vatPercentage,
      cost_type: "customs_btw",
    };

    onAddCustomsLine(mainLine);

    // Add separate voorschotprovisie line if there's interest
    if (paymentTerm && paymentTerm.interest_vat_import_duties > 0) {
      const interestAmount =
        btwValues.unitPrice * (paymentTerm.interest_vat_import_duties / 100);
      const interestLine = {
        description: `Voorschotprovisie BTW bij Invoer (${paymentTerm.interest_vat_import_duties}%)`,
        quantity: btwValues.quantity,
        unit_price: Math.round(interestAmount * 100) / 100, // Round to 2 decimals
        vat_percentage: btwValues.vatPercentage,
        cost_type: "customs_commission",
      };
      onAddCustomsLine(interestLine);
    }

    const linesAdded =
      paymentTerm && paymentTerm.interest_vat_import_duties > 0 ? 2 : 1;
    message.success(
      `BTW bij Invoer added to invoice lines (${linesAdded} line${
        linesAdded > 1 ? "s" : ""
      })`
    );

    // Reset values
    setBtwValues({
      quantity: 1,
      unitPrice: 0,
      vatPercentage: 0,
    });
  };

  // Add Invoerrechten to invoice lines
  const addInvoerrechtenToInvoiceLines = () => {
    if (invoerrechtenValues.unitPrice === 0) {
      message.error("Please enter a unit price for Invoerrechten");
      return;
    }

    // Main Invoerrechten line (original amount)
    const mainLine = {
      description: "Invoerrechten",
      quantity: invoerrechtenValues.quantity,
      unit_price: Math.round(invoerrechtenValues.unitPrice * 100) / 100, // Round to 2 decimals
      vat_percentage: invoerrechtenValues.vatPercentage,
      cost_type: "customs_duties",
    };

    onAddCustomsLine(mainLine);

    // Add separate voorschotprovisie line if there's interest
    if (paymentTerm && paymentTerm.interest_vat_import_duties > 0) {
      const interestAmount =
        invoerrechtenValues.unitPrice *
        (paymentTerm.interest_vat_import_duties / 100);
      const interestLine = {
        description: `Voorschotprovisie Invoerrechten (${paymentTerm.interest_vat_import_duties}%)`,
        quantity: invoerrechtenValues.quantity,
        unit_price: Math.round(interestAmount * 100) / 100, // Round to 2 decimals
        vat_percentage: invoerrechtenValues.vatPercentage,
        cost_type: "customs_commission",
      };
      onAddCustomsLine(interestLine);
    }

    const linesAdded =
      paymentTerm && paymentTerm.interest_vat_import_duties > 0 ? 2 : 1;
    message.success(
      `Invoerrechten added to invoice lines (${linesAdded} line${
        linesAdded > 1 ? "s" : ""
      })`
    );

    // Reset values
    setInvoerrechtenValues({
      quantity: 1,
      unitPrice: 0,
      vatPercentage: 0,
    });
  };

  if (!selectedBooking) {
    return null;
  }

  return (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        border: "2px solid #52c41a",
        backgroundColor: "#f6ffed",
      }}
      title={
        <span
          style={{
            color: "#52c41a",
            fontWeight: "bold",
            fontSize: "14px",
          }}
        >
          🛃 Customs
        </span>
      }
      bodyStyle={{ padding: "12px", backgroundColor: "#f6ffed" }}
    >
      {/* BTW bij Invoer */}
      <Row gutter={8}>
        <Col span={8}>
          <Form.Item label="BTW bij Invoer" style={{ marginBottom: 4 }}>
            <div style={{ padding: "4px 0", fontWeight: "bold" }}>
              {/* Label content removed to avoid duplication */}
            </div>
          </Form.Item>
        </Col>
        <Col span={16}>
          <Row gutter={8} justify="end">
            <Col span={4}>
              <Form.Item label="Quantity" style={{ marginBottom: 4 }}>
                <InputNumber
                  min={0}
                  value={btwValues.quantity}
                  onChange={(value) =>
                    setBtwValues((prev) => ({ ...prev, quantity: value || 1 }))
                  }
                  style={{ width: "100%" }}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Unit Price" style={{ marginBottom: 4 }}>
                <InputNumber
                  min={0}
                  step={0.01}
                  value={btwValues.unitPrice}
                  onChange={(value) =>
                    setBtwValues((prev) => ({ ...prev, unitPrice: value || 0 }))
                  }
                  onFocus={(e) => {
                    if (btwValues.unitPrice === 0) {
                      e.target.select();
                    }
                  }}
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Enter amount"
                  formatter={(value) =>
                    value
                      ? `€${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : "€0"
                  }
                  parser={(value) => {
                    const parsed = value
                      ? value.replace(/€\s?|(,*)/g, "")
                      : "0";
                    return parseFloat(parsed) as unknown as 0;
                  }}
                />
                {paymentTerm && paymentTerm.interest_vat_import_duties > 0 && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#fa8c16",
                      marginTop: "2px",
                    }}
                  >
                    +{paymentTerm.interest_vat_import_duties}% interest
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="VAT %" style={{ marginBottom: 4 }}>
                <InputNumber
                  min={0}
                  max={100}
                  value={btwValues.vatPercentage}
                  onChange={(value) =>
                    setBtwValues((prev) => ({
                      ...prev,
                      vatPercentage: value || 0,
                    }))
                  }
                  style={{ width: "100%" }}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="&nbsp;" style={{ marginBottom: 4 }}>
                <Button
                  type="primary"
                  size="small"
                  style={{ width: "100%" }}
                  onClick={addBtwToInvoiceLines}
                  disabled={btwValues.unitPrice === 0}
                >
                  Add
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Invoerrechten */}
      <Row gutter={8} style={{ marginTop: 8 }}>
        <Col span={8}>
          <Form.Item label="Invoerrechten" style={{ marginBottom: 4 }}>
            <div style={{ padding: "4px 0", fontWeight: "bold" }}>
              {/* Label content removed to avoid duplication */}
            </div>
          </Form.Item>
        </Col>
        <Col span={16}>
          <Row gutter={8} justify="end">
            <Col span={4}>
              <Form.Item label="Quantity" style={{ marginBottom: 4 }}>
                <InputNumber
                  min={0}
                  value={invoerrechtenValues.quantity}
                  onChange={(value) =>
                    setInvoerrechtenValues((prev) => ({
                      ...prev,
                      quantity: value || 1,
                    }))
                  }
                  style={{ width: "100%" }}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Unit Price" style={{ marginBottom: 4 }}>
                <InputNumber
                  min={0}
                  step={0.01}
                  value={invoerrechtenValues.unitPrice}
                  onChange={(value) =>
                    setInvoerrechtenValues((prev) => ({
                      ...prev,
                      unitPrice: value || 0,
                    }))
                  }
                  onFocus={(e) => {
                    if (invoerrechtenValues.unitPrice === 0) {
                      e.target.select();
                    }
                  }}
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Enter amount"
                  formatter={(value) =>
                    value
                      ? `€${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : "€0"
                  }
                  parser={(value) => {
                    const parsed = value
                      ? value.replace(/€\s?|(,*)/g, "")
                      : "0";
                    return parseFloat(parsed) as unknown as 0;
                  }}
                />
                {paymentTerm && paymentTerm.interest_vat_import_duties > 0 && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#fa8c16",
                      marginTop: "2px",
                    }}
                  >
                    +{paymentTerm.interest_vat_import_duties}% interest
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="VAT %" style={{ marginBottom: 4 }}>
                <InputNumber
                  min={0}
                  max={100}
                  value={invoerrechtenValues.vatPercentage}
                  onChange={(value) =>
                    setInvoerrechtenValues((prev) => ({
                      ...prev,
                      vatPercentage: value || 0,
                    }))
                  }
                  style={{ width: "100%" }}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="&nbsp;" style={{ marginBottom: 4 }}>
                <Button
                  type="primary"
                  size="small"
                  style={{ width: "100%" }}
                  onClick={addInvoerrechtenToInvoiceLines}
                  disabled={invoerrechtenValues.unitPrice === 0}
                >
                  Add
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

export default CustomsSection;
